import { z } from 'zod';
// s119 — canonical plan-tier limits. Single source of truth shared
// between the api (server-side enforcement in apps/api/src/plans.ts)
// and the dashboard (display in upgrade prompts, marketing copy).
//
// The DB stores only the plan discriminator (`organizations.plan` enum,
// see packages/db/src/schema/organizations.ts). All limits + display
// metadata live here so we can evolve them without a migration when we
// learn what the real ceilings should be.
//
// Mirrors the landing page's Founder Pack + Growth tiers (the only
// public plans today). Studio is intentionally absent — it doesn't
// exist as a self-serve tier until a Growth customer outgrows the
// limits below.
export const PLAN_KEYS = ['founder_pack', 'in_house', 'growth', 'agency'];
export const planKeySchema = z.enum(PLAN_KEYS);
/** Mag tokens granted per dollar of subscription. The ladder is built
 *  on this single ratio, so a new tier prices itself: tokens =
 *  round(price * TOKENS_PER_SUBSCRIPTION_USD).
 *
 *  Why it exists: before 2026-09-30 the markup DROPPED as the allowance
 *  grew (5000bps on founder_pack, 4000 on growth), so the bigger tier
 *  was structurally LESS profitable — a 9.6% floor against founder
 *  pack's 31%. The volume discount was being applied in the wrong
 *  direction for a cost-driven product. Uniform markup plus a fixed
 *  tokens-per-dollar ratio makes every rung clear the same floor.
 *
 *  WENT 20 -> 100 WITH THE REDENOMINATION [task c9032d2]. A mag token is now
 *  worth 1 cent of billed work rather than 5, so a dollar of subscription buys
 *  five times as many of them. The ladder's SHAPE is untouched: every rung's
 *  Anthropic allowance and floor margin are exactly what they were, because
 *  both ratio and unit moved by the same factor. */
export const TOKENS_PER_SUBSCRIPTION_USD = 100;
/** Markup on Anthropic pass-through, uniform across the ladder. The
 *  floor margin at FULL consumption is 1 - (1 / (1 + markup)) ≈ 33%;
 *  observed usage runs 30-60% of allowance, so realised margin is far
 *  higher. Raising this is the lever if the floor needs to move — it
 *  costs customers nothing until they are heavy users. */
export const UNIFORM_MARKUP_BPS = 5000;
export const PLANS = {
    founder_pack: {
        key: 'founder_pack',
        label: 'Founder Pack',
        basePriceUsd: 29,
        maxBrands: 1,
        maxSeats: 1,
        platforms: ['google', 'meta'],
        monthlyAnalyticsEvents: 10_000,
        // [s264] PLACEHOLDER — solo founder, 1 brand, light usage.
        // 600 tokens × $0.05/token = $30 of mag work covered by the $29 sub
        // (margin already baked into MAG_TOKEN_USD_CENTS). Calibrate after
        // Phase 1.5 caching data lands.
        includedLlmBudgetUsdCents: 5000,
        markupBps: UNIFORM_MARKUP_BPS,
        includedTokensPerCycle: 3000,
        maxAutonomy: 'A1',
    },
    in_house: {
        key: 'in_house',
        label: 'In-House',
        basePriceUsd: 49,
        maxBrands: 1,
        maxSeats: 2,
        platforms: ['google', 'meta'],
        monthlyAnalyticsEvents: 30_000,
        // "mag is my marketer" — the rung where she stops asking. Same one
        // brand as founder_pack on purpose: for a solo founder the binding
        // constraint is how much mag DOES, not how many brands they own.
        includedLlmBudgetUsdCents: 8333,
        markupBps: UNIFORM_MARKUP_BPS,
        includedTokensPerCycle: 5000,
        maxAutonomy: 'A0',
    },
    growth: {
        key: 'growth',
        label: 'Growth',
        basePriceUsd: 79,
        maxBrands: 3,
        maxSeats: 5,
        platforms: ['google', 'meta', 'tiktok'],
        monthlyAnalyticsEvents: 100_000,
        // [s264] PLACEHOLDER — multi-brand power user.
        // 2000 tokens × $0.05 = $100 of mag work, $79 sub. Lower markup vs
        // Founder Pack as a volume-discount feel.
        // 1600, down from 2000. At $79 with 2000 tokens the floor margin is
        // 15%; at 1600 it is 32% and consistent with every other rung.
        // Changed while ZERO orgs held a Stripe subscription — free to do
        // then, impossible after the first paying customer.
        includedLlmBudgetUsdCents: 13333,
        markupBps: UNIFORM_MARKUP_BPS,
        includedTokensPerCycle: 8000,
        maxAutonomy: 'A0',
    },
    agency: {
        key: 'agency',
        label: 'Agency',
        basePriceUsd: 199,
        maxBrands: 10,
        maxSeats: 15,
        platforms: ['google', 'meta', 'tiktok'],
        monthlyAnalyticsEvents: 500_000,
        includedLlmBudgetUsdCents: 33333,
        markupBps: UNIFORM_MARKUP_BPS,
        includedTokensPerCycle: 20000,
        maxAutonomy: 'A0',
    },
};
/** [s264] Mag-token unit value in USD cents. 1 token = $0.05 of billed
 *  mag work. PLACEHOLDER — final number calibrates after Phase 1.5
 *  caching data shows the real per-founder-month cost shape.
 *
 *  Marketing surface: "$49 = 1000 mag tokens, enough for: X Instagram
 *  posts drafted, weekly briefs all month, K competition refreshes,
 *  unlimited chat." Per-operation tokens documented in
 *  the mag-tokens doc (CommitMind) §2.
 *
 *  Conversion: tokensConsumedByLlmCall = ceil(
 *    anthropic_pass_through_cents × (1 + markup_bps/10000) / MAG_TOKEN_USD_CENTS
 *  )
 *  See tokensFromAnthropicCost() below.
 *
 *  ONE CENT, NOT FIVE, SINCE task c9032d2 — and the reason is the per-call
 *  floor below, not the arithmetic here.
 *
 *  Measured by replaying all 2,225 real ledger debits over 30 days: the old
 *  5-cent unit charged 1,550 tokens ($77.50) for $51.05 of billed work, a 52%
 *  over-charge, because `ceil` to a 5-cent grain rounds up hard on small
 *  calls. At a 1-cent grain the same debits charge $54.34 — 6.4% over, in the
 *  safe direction, with the billing principle untouched.
 *
 *  The floor's reach is what actually shrank. `meterLlmCall` already skips any
 *  call whose cost rounds to 0 cents, so the floor only ever applied between
 *  $0.005 and one token's worth of billed work: at 5 cents that was a
 *  $0.005-$0.033 band that agents like brand-coach sat inside on EVERY call
 *  (248 debits, $2.58 of cost, 248 tokens charged — 3.2x). At 1 cent the band
 *  is $0.005-$0.0067 and almost nothing lands in it.
 *
 *  Rejected: round-half-up with no floor. It reads unbiased and is not — the
 *  fractional parts here skew small, so replaying the same debits under it
 *  UNDER-bills by 8.4%, worst on founder_pack (10.6%), the plan with the least
 *  headroom. Rejected: a per-org fractional carry column, which is exact but
 *  adds state to the hot path for the 6.4% this leaves on the table.
 *
 *  Redenominating was free only because ZERO Stripe subscriptions existed.
 *  That window closes permanently with the first paying customer. */
export const MAG_TOKEN_USD_CENTS = 1;
/** Convert raw Anthropic pass-through cost (in cents) to consumed mag
 *  tokens, applying the plan's markup. Every LLM call decrements the
 *  org's balance by this amount. Always rounds UP — we never under-bill
 *  the customer (the rounding flows into magig's margin on small calls). */
export function tokensFromAnthropicCost(plan, anthropicCostUsdCents) {
    if (anthropicCostUsdCents <= 0)
        return 0;
    const def = PLANS[plan];
    const billedCents = (anthropicCostUsdCents * (10000 + def.markupBps)) / 10000;
    return Math.max(1, Math.ceil(billedCents / MAG_TOKEN_USD_CENTS));
}
// ─── Top-up packs ───────────────────────────────────────────────────
//
// One-time Stripe Checkout purchases that grant mag tokens. Founders
// buy these to refill their balance or push past the subscription's
// included tokens for the cycle. Higher packs get a small volume
// discount to nudge toward larger purchases.
export const TOPUP_PACK_KEYS = ['small', 'medium', 'large'];
export const topupPackKeySchema = z.enum(TOPUP_PACK_KEYS);
export const TOPUP_PACKS = {
    small: {
        key: 'small',
        tokens: 5000,
        priceUsd: 49,
        label: '5,000 mag tokens',
        // THE LOOKUP KEY DOES NOT MOVE WITH THE TOKEN COUNT [task c9032d2].
        //
        // It is a Stripe price identifier, not a description. Renaming it to
        // match the new count would stop resolving if a price with this key has
        // ever been created in Stripe — and the redenomination cannot verify that
        // from here. A key whose name under-states its contents is a wart; a
        // top-up checkout that cannot find its price is an outage, so this fails
        // in the safe direction. Rename it only alongside the Stripe seeding that
        // the pricing-ladder doc still lists as not done.
        stripeLookupKey: 'mag_tokens_1000',
    },
    medium: {
        key: 'medium',
        tokens: 25000,
        // ~6.5% volume discount vs small.
        priceUsd: 229,
        label: '25,000 mag tokens',
        // Same reasoning as small: the key is Stripe's, the number is ours.
        stripeLookupKey: 'mag_tokens_5000',
    },
    large: {
        key: 'large',
        tokens: 50000,
        // ~12.5% volume discount vs small.
        priceUsd: 429,
        label: '50,000 mag tokens',
        // Same reasoning as small: the key is Stripe's, the number is ours.
        stripeLookupKey: 'mag_tokens_10000',
    },
};
export function getTopupPack(key) {
    return TOPUP_PACKS[key];
}
/** [s264] Resolve the included LLM budget for an org given its plan +
 *  optional per-org override. The override (organizations
 *  .custom_included_llm_budget_usd_cents) lets ops carve out
 *  negotiated deals without rewriting the plan map. */
export function resolveIncludedLlmBudgetUsdCents(plan, customOverrideCents) {
    if (customOverrideCents !== null && customOverrideCents >= 0) {
        return customOverrideCents;
    }
    return PLANS[plan].includedLlmBudgetUsdCents;
}
export function getPlan(key) {
    return PLANS[key];
}
/** True if `plan` permits adding one more brand given the current count.
 *  Centralised so both the api and the dashboard's pre-submit gate
 *  speak the same rule. */
export function canAddBrand(plan, currentBrandCount) {
    return currentBrandCount < PLANS[plan].maxBrands;
}
/** True if `plan` permits adding one more seat. Counts owner + admins +
 *  members + pending invites. Pre-invite-flow Founder Pack hits the cap
 *  at 1 (just the owner) — the route returns 402 with the upsell. */
export function canAddSeat(plan, currentSeatCount) {
    return currentSeatCount < PLANS[plan].maxSeats;
}
/** True if `plan` permits connecting an ad account on the given platform.
 *  Used by both the connect endpoint (gate) and the dashboard
 *  connect-buttons (disable + show upgrade prompt). */
export function canConnectPlatform(plan, platform) {
    return PLANS[plan].platforms.includes(platform);
}
/** The next plan up from `plan`, or null if already at the top of the
 *  ladder. Used by upgrade prompts ("Growth covers 3 brands"). When
 *  Studio ships this function will return 'growth' → 'studio'. */
export function nextPlanUp(plan) {
    if (plan === 'founder_pack')
        return 'growth';
    return null;
}
//# sourceMappingURL=plans.js.map